import type { Game, Opponent, Player, Serve } from "../types";

// Game and reception analytics. A serve with a gameId was recorded during that
// game; receptionGrade is the opposing receiver's pass, graded on the scale
// stored with it (0–4 by default). Grades are compared on a 0–1 basis so
// serves graded on different scales can be pooled.

export type Scale = [number, number];

export const defaultScale: Scale = [0, 4];

export function normalizedGrade(serve: Serve): number | null {
  if (serve.receptionGrade == null || !serve.receptionScale) return null;
  const [min, max] = serve.receptionScale;
  return max > min ? (serve.receptionGrade - min) / (max - min) : null;
}

/** The scale most graded serves use — averages are shown on it. */
export function displayScale(serves: Serve[]): Scale {
  const counts = new Map<string, { scale: Scale; n: number }>();
  for (const serve of serves) {
    if (!serve.receptionScale || serve.receptionGrade == null) continue;
    const key = serve.receptionScale.join("-");
    const entry = counts.get(key) ?? { scale: serve.receptionScale, n: 0 };
    entry.n += 1;
    counts.set(key, entry);
  }
  return [...counts.values()].sort((a, b) => b.n - a.n)[0]?.scale ?? defaultScale;
}

export function onScale(normalized: number, scale: Scale) {
  return scale[0] + normalized * (scale[1] - scale[0]);
}

export function seasonOf(game: Game) {
  return game.season || "—";
}

type Tally = {
  serves: number;
  aces: number;
  misses: number;
  speedSum: number;
  graded: number;
  gradeSum: number;       // normalized 0–1
  perfect: number;        // top-of-scale passes
};

function emptyTally(): Tally {
  return { serves: 0, aces: 0, misses: 0, speedSum: 0, graded: 0, gradeSum: 0, perfect: 0 };
}

function add(t: Tally, serve: Serve) {
  t.serves += 1;
  if (serve.state === 3) t.aces += 1;
  if (serve.state === 1) t.misses += 1;
  t.speedSum += serve.speed;
  const g = normalizedGrade(serve);
  if (g != null) {
    t.graded += 1;
    t.gradeSum += g;
    if (g >= 1) t.perfect += 1;
  }
}

export type TallyStats = {
  serves: number;
  aces: number;
  misses: number;
  aceRate: number;        // %
  missRate: number;       // %
  avgSpeed: number;       // km/h
  graded: number;
  avgGrade: number | null; // normalized 0–1
  perfectRate: number | null; // % of graded passes at the top of the scale
};

function finish(t: Tally): TallyStats {
  return {
    serves: t.serves,
    aces: t.aces,
    misses: t.misses,
    aceRate: t.serves ? (t.aces / t.serves) * 100 : 0,
    missRate: t.serves ? (t.misses / t.serves) * 100 : 0,
    avgSpeed: t.serves ? t.speedSum / t.serves : 0,
    graded: t.graded,
    avgGrade: t.graded ? t.gradeSum / t.graded : null,
    perfectRate: t.graded ? (t.perfect / t.graded) * 100 : null
  };
}

export function summarize(serves: Serve[]): TallyStats {
  const t = emptyTally();
  serves.forEach((serve) => add(t, serve));
  return finish(t);
}

function groupBy<K>(serves: Serve[], key: (serve: Serve) => K | null | undefined) {
  const groups = new Map<K, Tally>();
  for (const serve of serves) {
    const k = key(serve);
    if (k == null) continue;
    const t = groups.get(k) ?? emptyTally();
    add(t, serve);
    groups.set(k, t);
  }
  return groups;
}

/** Opposing receivers, weakest passers first (fewest samples last among ties). */
export function receiverStats(serves: Serve[]) {
  return [...groupBy(serves, (serve) => serve.receiverNumber).entries()]
    .map(([number, t]) => ({ number, ...finish(t) }))
    .sort((a, b) => (a.avgGrade ?? 2) - (b.avgGrade ?? 2) || b.serves - a.serves);
}

export function gameStats(serves: Serve[], games: Game[], opponents: Opponent[]) {
  const tallies = groupBy(serves, (serve) => serve.gameId);
  return games
    .filter((game) => tallies.has(game.id))
    .map((game) => ({
      game,
      opponent: opponents.find((o) => o.id === game.opponentId)?.name ?? "Opponent",
      ...finish(tallies.get(game.id)!)
    }));
}

export function serverStats(serves: Serve[], players: Player[]) {
  return [...groupBy(serves, (serve) => serve.playerId || null).entries()]
    .map(([playerId, t]) => {
      const player = players.find((p) => p.id === playerId);
      return {
        playerId,
        name: player ? `${player.firstName} ${player.lastName}`.trim() : "Unassigned",
        number: player?.number,
        ...finish(t)
      };
    })
    .sort((a, b) => b.serves - a.serves);
}

/** Per player: the same serve in practice vs in games. */
export function practiceVsGame(serves: Serve[], players: Player[]) {
  return players
    .map((player) => {
      const own = serves.filter((serve) => serve.playerId === player.id);
      const practice = summarize(own.filter((serve) => !serve.gameId));
      const game = summarize(own.filter((serve) => serve.gameId));
      return { name: player.firstName, practice, game };
    })
    .filter((row) => row.practice.serves > 0 && row.game.serves > 0);
}
